import { Injectable } from '@angular/core';
import { ActivatedRoute, Router } from "@angular/router";
import { HeaderData } from 'src/app/_models/header-data';
import { API_URL, SUBMISSION_URL } from '../_common/constants';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { map, switchMap, retryWhen, concatMap, delay } from 'rxjs/operators';
import { of, Observable, throwError, iif } from 'rxjs';
import { StatusCode, UserType, Modules, PSIOperators, DocumentExtensionEnum, DocumentTypeEnum } from '../_common/_enum/module.enum'; //USING IT IN HTML
import { POLLING_CONFIG } from '../_common/product-config';

@Injectable({
  providedIn: 'root'
})
export class CommonService {

  private headerData:HeaderData;
  private readonly route: ActivatedRoute;
  private readonly router: Router;
  private documentExtEnum = DocumentExtensionEnum;
  private documentTypeEnum = DocumentTypeEnum;
  private impersonatedBy = null;
  
  private killServicesOnDestroy = [];

  private supportingDocuments = [];

  private lookupReferenceData = {};

  private dnBData = {};
  private _psiData:any;
  
  constructor(private http:HttpClient) {
    this.headerData = new HeaderData();
    this._psiData = this.getData();
   }

  storeData(psiData){
    // store psi data to session storage
    this._psiData = psiData;
    try{
      sessionStorage.setItem('psiData', JSON.stringify(psiData));
    }catch(error){
      console.log("Error in storeData : " + error);
    }
  }

  getData(){
    // get psi data from session storage
    return JSON.parse(sessionStorage.getItem('psiData'));
  }

  removeData(){
    console.log("removing storage data");
    // remove psi data from session storage
    sessionStorage.removeItem('psiData');
  }

  /** JUST TO STORE LOOKUPS FOR SUBMISSIONS AND CLEARNACE */
  storeLocalStorageData(psiData){
    // store psi lookpup to Session storage
    try{
      sessionStorage.setItem('psiLookup', JSON.stringify(psiData));
    }catch(error){
      console.log("Error in storeSessionStorageData : " + error);
    }
  }

  getLocalStorageData(){
    // get psi lookpup from Session storage
    return JSON.parse(sessionStorage.getItem('psiLookup'));
  }

  removeLocalStorageData(){
    console.log("removing Session storage data");
    // remove psi lookpup from Session storage
    sessionStorage.removeItem('psiLookup');
  }
  /** JUST TO STORE LOOKUPS FOR SUBMISSIONS AND CLEARNACE */

  /* Below function is specifically to grab ID from route for any component */
  /* Need to fix this function */
  getId() {
    return this.route.paramMap.subscribe(params => {
      return params.get("id").toString();
    });
  }
  
  set header(_data:HeaderData){
    setTimeout(()=>{
      this.headerData = _data;
    }, 100);
  }

  get header(){
    return this.headerData;
  }
  
  // killServicesOnDestroy accepts subscriptions from entire application. We are killing it on ngOnDestroy of all the component and also when needed.
  get runningServices(){
    return this.killServicesOnDestroy;
  }
  
  set viewingAs(_data){
    setTimeout(()=>{
      let psiData =  this.getData();
      psiData.impersonatedBy = _data;
      this.storeData(psiData);
      this.impersonatedBy = psiData.impersonatedBy;
    }, 100);
  }

  get viewingAs(){
    if(this.impersonatedBy == null){
      
      if(!this._psiData){
        this._psiData = this.getData();
      }
      if(this._psiData.impersonatedBy == null){
        this._psiData.impersonatedBy = {"companyName":this._psiData.currentUser.companyName, "companyId":this._psiData.currentUser.companyID };
        this.storeData(this._psiData);
      }
      this.impersonatedBy = this._psiData.impersonatedBy;
    }
    return this.impersonatedBy;
  }

  get isProSightUser(){
    if(!this._psiData){
      this._psiData = this.getData();
    }
    return (this._psiData && this._psiData.currentUser && this._psiData.currentUser.userType && this._psiData.currentUser.userType === UserType.ProSight)
  }
  
  get isSuperAdmin(){
    return this.checkPermission(Modules.AdminUtil);
  }
  
  // Check individual Module permission..
  checkPermission(moduleName:string):boolean{
    if(!moduleName) console.error("Missing Module Name");
    if(!this._psiData){
      this._psiData = this.getData();
    }
    if(this._psiData.currentUser.permissions){
      return (moduleName in this._psiData.currentUser.permissions && this._psiData.currentUser.permissions[moduleName] != null && this._psiData.currentUser.permissions[moduleName] != undefined);
    }else {
      return false;
    }
  }
  // Check multiple Module permission..
  checkPermissions(moduleName:string[], operator:string ):boolean{
    let hasAccess:boolean = false;
    let orOp:boolean = operator == PSIOperators.OR;
    let andOp:boolean = operator == PSIOperators.AND;
    
    for(let i=0; i<moduleName.length; i++){
      let authorize = this.checkPermission(moduleName[i]);
      
      if(authorize){
        hasAccess = true;
        if(orOp){
          break;
        }
      }else{
        hasAccess = false;
        if(andOp){
          break;
        }
      }
    }
    return hasAccess;
  }
  /**
   * //#region  For Polling
   * @param taskId
   */
  queueTask(taskId: string){
    return this.http.get<any>(API_URL.QueueTask+ "/" + taskId, {observe: 'response'})
    .pipe(map((response:any) => {
      if(response.status.toString() == StatusCode.Polling){
        throw StatusCode.Polling;
      }
      return response.body.result;
 
    }),
    retryWhen(response => {
        return response.pipe(
            concatMap((error, attempts) => {
                if (error !== StatusCode.Polling) {
                    return throwError(error.body.result);
                }
                return iif(
                    () => attempts >= POLLING_CONFIG.MaxPollingAttempt,
                    throwError(error),
                    of(error).pipe(delay(POLLING_CONFIG.PollingInterval))
                )
            })
        )
    }))
  }
  /** //#endregion For Polling */  

  /* 
    Using below method in multiple Modules.
  */
  fetchAccountByPolicy(policyNumber){
    return this.http.get<any>(API_URL.AccountByPolicy+ "?policyNumber="+policyNumber, {observe: 'response'})
        .pipe(map(searchData => {
            return searchData.body.result;
        }));
  }

  fetchFAQs(){
    return this.http.get<any>(API_URL.FAQ, {observe: 'response'})
        .pipe(map(faqData => {
            return faqData.body.result;
        }));
  }

  fetchPrivacyPolicy(){
    return this.http.get<any>(API_URL.PrivacyPolicy, {observe: 'response'})
        .pipe(map(privacyPolicyData => {
            return privacyPolicyData.body.result;
        }));
  }


  /*** Adding, Getting & Setting Documents Uploaded by Broker / User ***/
  addSupportingDocument(_document){
    this.supportingDocuments.push(_document);
  }

  getSupportingDocuments(){
    return this.supportingDocuments;
  }
  setSupportingDocuments(_documents){
    this.supportingDocuments = _documents;
  }
  resetSupportingDocuments(){
    this.supportingDocuments = [];
  }
  /*** Adding, Getting & Setting Documents Uploaded by Broker / User ***/

  get referenceData(){
    let psiLocalStorageData =  this.getLocalStorageData();
    if(psiLocalStorageData){
      this.lookupReferenceData = psiLocalStorageData.lookup;
      return this.lookupReferenceData;
    }else{
      return null;
    }
  }

  set referenceData(_data){
    let psiLocalStorageData =  this.getLocalStorageData();
    if(!psiLocalStorageData){
      psiLocalStorageData = {lookup: {}};
    }
    psiLocalStorageData.lookup = _data;
    this.lookupReferenceData = _data;

    this.storeLocalStorageData(psiLocalStorageData);
  }

  
  fetchReferenceData(){
    let _referenceData = this.referenceData;
    if(_referenceData == null){
      return this.http.get<any>(SUBMISSION_URL.LookupList, {observe: 'response'})
        .pipe(map(response => {
          this.referenceData = response.body.result;
          return response.body.result;
      }));
    }else{
      return of(_referenceData);
    }
  }
  
  fetchPolicyDocumentList(request) {
     return this.http.post<any>(API_URL.PolicyDocuments, request, {observe: 'response'})
        .pipe(map(policyDocumentData => {
            return policyDocumentData.body.result;
        }));
  }

  storeDnBEventData(psiData){
    // store psi data to session storage
    try{
      sessionStorage.setItem('psiDnBEvent', JSON.stringify(psiData));
    }catch(error){
      console.log("Error in storeSessionStorageData : " + error);
    }
  }

  getDnBEventData(){
    // get psi data from session storage
    return JSON.parse(sessionStorage.getItem('psiDnBEvent'));
  }

  removeDnBEventData(){
    console.log("removing storage data");
    // remove psi data from session storage
    sessionStorage.removeItem('psiDnBEvent');
  }

  get dnbEventData(){
    let _psiDnBEvent =  this.getDnBEventData();
    if(_psiDnBEvent){
      this.dnBData = _psiDnBEvent.psiDnBEvent;
      return this.dnBData;
    }else{
      return null;
    }
  }

  set dnbEventData(_data){
    let _psiDnBEvent =  this.getDnBEventData();
    if(!_psiDnBEvent){
      _psiDnBEvent = {eventData: {}};
    }
    _psiDnBEvent.eventData = _data;
    this.dnBData = _data;

    this.storeDnBEventData(_psiDnBEvent);
  }

  setdandbevent(_data: any) {
    return this.http.post<any>(API_URL.Events, _data, {observe: 'response'})
        .pipe(map(response => {
          return response.body.result;
    }));
  }

  setFileIcon(file:any){
    let name = (file.name) ? file.name.toLowerCase() : file.fileName.toLowerCase();
    let type = (file.type) ? file.type : file.fileType;
    type = 'application/' + type;
    return (type == this.documentTypeEnum.Word || type == this.documentTypeEnum.WordZip || type == this.documentTypeEnum.WordMacro || name.includes(this.documentExtEnum.Word) || name.includes(this.documentExtEnum.WordZip) || name.includes(this.documentExtEnum.WordMacro)) ? 'word' : 
    (type == this.documentTypeEnum.Excel || type == this.documentTypeEnum.ExcelZip || type == this.documentTypeEnum.ExcelMacro || type == this.documentTypeEnum.ExcelComma || name.includes(this.documentExtEnum.Excel) || name.includes(this.documentExtEnum.ExcelZip) || name.includes(this.documentExtEnum.ExcelMacro) || name.includes(this.documentExtEnum.ExcelComma)) ? 'excel' : 
    (type == this.documentTypeEnum.PDF || name.includes(this.documentExtEnum.PDF) ? 'pdf' : 'file')
  }
}
